import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import InfoTooltip from '@/components/InfoTooltip.jsx';
import './GameRecordingControls.css';

export default function GameRecordingControls({
  controller, canControl, gameType, sessionStatus, t,
  speakingIdentities = [], audioDiagnostic = null,
  audioConnected = false, onEnableAudio,
}) {
  const {
    status, busy, error, start, stop, decideVoice, requestVoice,
    consentModalRequested, setConsentModalRequested, setError,
  } = controller;
  const [manageOpen, setManageOpen] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [localError, setLocalError] = useState('');
  const [requested, setRequested] = useState([]);
  const firstChoiceRef = useRef(null);
  const active = Boolean(status.enabled && status.active);
  const canStart = status.enabled && canControl
    && (gameType === 'group' || gameType === 'individual');
  const decision = status.voiceDecision || 'pending';
  const needsChoice = active && !canControl && decision === 'pending';
  const modalVisible = active && (manageOpen || needsChoice || (!canControl && consentModalRequested));
  const playerChoice = !canControl && (needsChoice || (manageOpen && decision !== 'allowed'));
  const playerStop = !canControl && manageOpen && decision === 'allowed';

  useEffect(() => {
    if (!active) {
      setManageOpen(false);
      setConfirmStop(false);
      setConsentModalRequested(false);
    }
  }, [active, setConsentModalRequested]);

  useEffect(() => {
    if (playerChoice) firstChoiceRef.current?.focus();
  }, [playerChoice]);

  if (!canStart && !active) return null;

  const close = () => {
    setManageOpen(false);
    setConfirmStop(false);
    setConsentModalRequested(false);
    setLocalError('');
    setError('');
  };

  const handleRecordingButton = async () => {
    if (busy) return;
    if (active) {
      setManageOpen(true);
      return;
    }
    if (canStart && sessionStatus === 'running') {
      const ok = await start();
      if (ok) setManageOpen(true);
    }
  };

  const chooseVoice = async (allowed) => {
    setLocalError('');
    // A browser gesture must request the microphone before any asynchronous
    // API call. Never pretend that voice consent granted microphone access.
    if (allowed && !audioConnected) {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('NO_MEDIA_DEVICES');
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true, video: false,
        });
        stream.getTracks().forEach((track) => track.stop());
      } catch {
        setLocalError(t('recording.microphoneError'));
        return;
      }
    }
    if (await decideVoice(allowed)) {
      if (!canControl) close();
      if (allowed && !audioConnected) onEnableAudio?.();
    }
  };

  const askAgain = async (actorIdentity) => {
    if (await requestVoice(actorIdentity)) {
      setRequested((old) => [...old.filter((v) => v !== actorIdentity), actorIdentity]);
    }
  };

  const stateText = (value) => t('recording.' + (
    value === 'allowed' ? 'voiceAllowedShort'
      : value === 'denied' ? 'voiceDeniedShort' : 'voicePendingShort'
  ));

  const canStop = Boolean(canStart && active);
  const savedAudioSegments = Math.max(0,
    Number(status.recording?.readyAudioSegments || 0));
  const capturingAudio = audioDiagnostic?.captureState === 'capturing'
    && ['ready', 'buffering'].includes(audioDiagnostic?.uploadState);
  const audioUploadBlocked = Boolean(audioDiagnostic?.error)
    || audioDiagnostic?.uploadState === 'buffering';
  // The green state is ONLY for confirmed server-side audio fragments.
  // Active speaker events and MediaRecorder are insufficient proof of storage.
  const showAudioWarning = !audioConnected || audioUploadBlocked
    || savedAudioSegments === 0;
  const participants = Array.isArray(status.participants)
    ? status.participants.filter((participant) => participant.identity.startsWith('p_'))
    : [];

  const modal = modalVisible && (
    <div className="game-recording-modal-overlay" role="presentation">
      <div className="game-recording-modal game-recording-modal--compact"
        role={confirmStop ? 'alertdialog' : 'dialog'} aria-modal="true"
        aria-labelledby="game-recording-active-title">
        {canControl ? (
          <>
            <div className="game-recording-modal__heading">
              <span className="game-recording-rec"><span className="game-recording-rec__dot" /> REC</span>
              <h2 id="game-recording-active-title">
                {confirmStop ? t('recording.stopConfirmTitle') : t('recording.manageRecording')}
              </h2>
            </div>
            {confirmStop ? (
              <>
                <p>{t('recording.stopConfirmDescription')}</p>
                <p className="game-recording-modal__retention">{t('recording.resumeNotice')}</p>
                <div className="game-recording-modal__buttons">
                  <button type="button" className="game-recording-modal__deny"
                    disabled={busy} onClick={() => setConfirmStop(false)}>{t('recording.cancel')}</button>
                  <button type="button" className="game-recording-modal__stop"
                    disabled={busy} onClick={async () => { if (await stop()) close(); }}>
                    {t('recording.confirmStop')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="game-recording-consent-heading">{t('recording.participantStatus')}</h3>
                {participants.length ? (
                  <ul className="game-recording-participant-list">
                    {participants.map((participant) => (
                      <li key={participant.identity}>
                        <span>{participant.name}</span>
                        <strong className={'game-recording-decision game-recording-decision--' + participant.decision}>
                          {participant.decision === 'allowed'
                            && speakingIdentities.includes(participant.identity)
                            ? t('recording.speakingNow') : stateText(participant.decision)}
                        </strong>
                        {participant.decision !== 'allowed' && (
                          <button type="button" className="game-recording-request-btn"
                            disabled={busy} onClick={() => askAgain(participant.identity)}>
                            {requested.includes(participant.identity) && participant.decision === 'pending'
                              ? t('recording.requestSent') : t('recording.requestAgain')}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : <p className="game-recording-modal__retention">{t('recording.voicePendingShort')}</p>}
                <div className="game-recording-self-voice">
                  <span>{t('recording.ownVoice')}: {stateText(decision)}</span>
                  <button type="button" className="game-recording-request-btn" disabled={busy}
                    onClick={() => chooseVoice(decision !== 'allowed')}>
                    {decision === 'allowed'
                      ? t('recording.voiceDisableSelf') : t('recording.voiceAllowSelf')}
                  </button>
                </div>
                <p className={showAudioWarning ? 'game-recording-audio-warning' : 'game-recording-audio-ready'}
                  role="status">
                  {!audioConnected ? t('recording.audioNotConnected')
                    : audioUploadBlocked ? t('recording.audioUploadRetry')
                      : savedAudioSegments > 0
                        ? t('recording.audioSavedCount', { count: savedAudioSegments })
                        : capturingAudio ? t('recording.audioAwaitingSave')
                          : t('recording.audioNotYet')}
                </p>
                {showAudioWarning && (
                  <button type="button" className="game-recording-audio-connect"
                    onClick={onEnableAudio}>{t('recording.audioConnect')}</button>
                )}
                {audioDiagnostic?.error && (
                  <p className="game-recording-error">{audioDiagnostic.error}</p>
                )}
                <div className="game-recording-modal__buttons">
                  <button type="button" className="game-recording-modal__deny"
                    onClick={close}>{t('recording.close')}</button>
                  {canStop && (
                    <button type="button" className="game-recording-modal__stop"
                      onClick={() => setConfirmStop(true)}>{t('recording.stop')}</button>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <h2 id="game-recording-active-title">
              {playerStop ? t('recording.playerStopTitle') : t('recording.consentTitle')}
            </h2>
            <p>{playerStop
              ? t('recording.playerStopDescription') : t('recording.consentShort')}</p>
            {playerStop ? (
              <div className="game-recording-modal__buttons">
                <button type="button" className="game-recording-modal__stop"
                  disabled={busy} onClick={() => chooseVoice(false)}>
                  {t('recording.stopMyVoice')}
                </button>
                <button type="button" className="game-recording-modal__deny"
                  onClick={close}>{t('recording.close')}</button>
              </div>
            ) : (
              <div className="game-recording-modal__buttons">
                <button type="button" ref={firstChoiceRef}
                  className="game-recording-modal__allow"
                  disabled={busy} onClick={() => chooseVoice(true)}>
                  {t('recording.allowShort')}
                </button>
                <button type="button" className="game-recording-modal__deny"
                  disabled={busy} onClick={() => chooseVoice(false)}>
                  {t('recording.denyShort')}
                </button>
              </div>
            )}
          </>
        )}
        {(localError || error) && (
          <p className="game-recording-error" role="alert">
            {localError || t('recording.error') + ': ' + error}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <>
      <InfoTooltip content={active ? t('recording.manageRecording') : t('recording.start')}>
        <button type="button" className={'sidebar-action game-recording-sidebar-button' +
          (active ? ' active game-recording-sidebar-button--active' : '')}
          aria-label={active ? t('recording.manageRecording') : t('recording.start')}
          aria-pressed={active} disabled={busy || (!active && sessionStatus !== 'running')}
          onClick={handleRecordingButton}>
          <svg viewBox="0 0 48 48" role="presentation" aria-hidden="true">
            <circle cx="24" cy="24" r="16.5" fill="none" stroke="currentColor" strokeWidth="3" />
            <circle cx="24" cy="24" r="9" fill="currentColor" />
          </svg>
          {active && <span className="game-recording-sidebar-rec" aria-hidden="true">REC</span>}
          {error && !active && <span className="game-recording-sidebar-error" aria-hidden="true">!</span>}
        </button>
      </InfoTooltip>
      {typeof document !== 'undefined' && createPortal(modal, document.body)}
    </>
  );
}
